import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { useEffect, useMemo, useRef, useCallback, useState } from 'react'

interface NarrativeEditorProps {
  content: string
  onChange?: (content: string) => void
  readOnly?: boolean
}

function useDebouncedCallback<T extends (...args: Parameters<T>) => void>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(() => callbackRef.current(...args), delay)
    }) as T,
    [delay]
  )
}

function processContent(content: string): string {
  if (!content) return ''
  if (content.trim().startsWith('<')) {
    return content
  }

  // Convert markdown to HTML
  let html = content

  // Process bold: **text** -> <strong>text</strong>
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

  // Process italic: *text* or _text_ -> <em>text</em>
  html = html.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>')

  // Split into blocks for paragraph and list processing
  const blocks = html.split(/\n\n+/)

  const processedBlocks = blocks.map(block => {
    const lines = block.split('\n')

    // Check if this is a list block (all non-empty lines start with -)
    const nonEmptyLines = lines.filter(l => l.trim())
    const isUnorderedList = nonEmptyLines.length > 0 &&
      nonEmptyLines.every(l => l.trim().startsWith('- '))

    if (isUnorderedList) {
      const items = nonEmptyLines
        .map(l => `<li>${l.trim().slice(2)}</li>`)
        .join('')
      return `<ul>${items}</ul>`
    }

    // Check for ordered list (lines start with number.)
    const isOrderedList = nonEmptyLines.length > 0 &&
      nonEmptyLines.every(l => /^\d+\.\s/.test(l.trim()))

    if (isOrderedList) {
      const items = nonEmptyLines
        .map(l => `<li>${l.trim().replace(/^\d+\.\s/, '')}</li>`)
        .join('')
      return `<ol>${items}</ol>`
    }

    // Process single lines for headings or regular paragraphs
    return lines.map(line => {
      const trimmed = line.trim()
      if (!trimmed) return ''

      // Headings
      if (trimmed.startsWith('### ')) {
        return `<h3>${trimmed.slice(4)}</h3>`
      }
      if (trimmed.startsWith('## ')) {
        return `<h2>${trimmed.slice(3)}</h2>`
      }
      if (trimmed.startsWith('# ')) {
        return `<h1>${trimmed.slice(2)}</h1>`
      }

      return `<p>${trimmed}</p>`
    }).filter(Boolean).join('')
  })

  return processedBlocks.join('')
}

export function NarrativeEditor({ content, onChange, readOnly = false }: NarrativeEditorProps) {
  const hasLocalEditsRef = useRef(false)
  const lastSavedContentRef = useRef(content)
  const [, forceUpdate] = useState(0)

  const htmlContent = useMemo(() => processContent(content), [content])

  const debouncedOnChange = useDebouncedCallback((html: string) => {
    onChange?.(html)
    lastSavedContentRef.current = html
    setTimeout(() => { hasLocalEditsRef.current = false }, 1500)
  }, 1000)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder: 'Your story will unfold here as you collaborate with the AI director...',
      }),
    ],
    content: htmlContent,
    editable: !readOnly,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      hasLocalEditsRef.current = true
      debouncedOnChange(editor.getHTML())
    },
    onSelectionUpdate: () => {
      forceUpdate(n => n + 1)
    },
    onTransaction: () => {
      forceUpdate(n => n + 1)
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[200px] p-4 text-secondary-foreground text-sm [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-foreground [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-secondary-foreground [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-secondary-foreground [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:mb-2 [&_strong]:text-secondary-foreground [&_strong]:font-semibold [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:mb-1',
      },
    },
  })

  useEffect(() => {
    if (!editor) return
    if (hasLocalEditsRef.current) return
    if (content === lastSavedContentRef.current) return

    const processed = processContent(content)
    if (processed !== editor.getHTML()) {
      // emitUpdate: false prevents onUpdate from firing, which would echo the
      // server content back as a "user edit" and trigger [NARRATIVE_UPDATED]
      editor.commands.setContent(processed, { emitUpdate: false })
      lastSavedContentRef.current = content
    }
  }, [content, editor])

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">

      <EditorContent editor={editor} />
    </div>
  )
}
